/**
 * English "near me" copy — wave 2: the remaining (non-flagship) clusters.
 *
 * Hand-authored, zero AI credits at build or runtime. Factual guardrails kept
 * identical to wave 1:
 *  - Thailand is NOT a Hague Apostille state; outbound Thai documents go
 *    through the Legalization Division, Department of Consular Affairs (MFA)
 *    and, when asked for, the destination embassy in Thailand.
 *  - No published prices, no turnaround guarantees, no promise of acceptance
 *    or approval — that decision belongs to the receiving authority.
 *  - Advisory framing: we advise on sequence and evidence, not just execute.
 */

import type { NearServiceKey } from "./registry";
import type { NearEnCopy } from "./en-copy";

export const NEAR_EN2_KEYS = [
  "marriage",
  "longstay",
  "corporate",
  "immigration",
  "estate",
  "civilreg",
  "family",
  "accounting",
  "interpreter",
  "embassy",
  "ip",
  "litigation",
  "pdpa",
  "misc-official",
  "council",
  "due-diligence",
  "pension",
  "work-permit",
  "expert",
] as const satisfies readonly NearServiceKey[];

export type NearEn2Key = (typeof NEAR_EN2_KEYS)[number];

export const NEAR_EN2_COPY: Record<NearEn2Key, NearEnCopy> = {
  marriage: {
    label: "Thai–foreign marriage document service",
    definition:
      "Registering a Thai–foreign marriage is a document sequence rather than a single appointment: an affirmation of freedom to marry from your embassy in Thailand, certified Thai translation, MFA legalization of that translation, then registration at a district office (amphoe).",
    chain: [
      "Confirm your embassy's current affirmation or single-status procedure and booking rules.",
      "Obtain the affirmation, then have it translated by a certified translator.",
      "Legalize the translation at the Legalization Division (MFA).",
      "Register the marriage at the district office with both parties present.",
      "Order the marriage certificate set, translations and legalization for use abroad.",
    ],
    checklist: [
      "Passport of the foreign party with entry stamp.",
      "Thai ID and house registration of the Thai party.",
      "Embassy affirmation of freedom to marry (or single-status document).",
      "Divorce or death certificate where a previous marriage existed.",
      "Two witnesses for the district-office registration.",
    ],
    pitfalls: [
      "Booking the district office before the MFA legalization is complete.",
      "Name spelling on the affirmation that differs from the passport.",
      "Missing proof of termination of a previous marriage.",
      "Assuming a Thai marriage certificate is automatically accepted abroad without translation and legalization.",
    ],
    remote:
      "Translation, legalization and sequencing are handled for you; the embassy appointment and the registration itself need both parties in person.",
  },
  longstay: {
    label: "Long-stay and retirement extension document work",
    definition:
      "Long-stay extensions are decided by the Immigration Bureau on the evidence filed. Our role is document preparation and advisory: matching the evidence set to the category, in the format immigration accepts.",
    chain: [
      "Identify the correct extension category and the current evidence list.",
      "Gap analysis against the documents you already hold.",
      "Prepare financial, address and insurance evidence in the accepted format.",
      "Assemble the file in filing order with a submission checklist.",
      "Support during filing and respond to any request for further evidence.",
    ],
    checklist: [
      "Passport with current visa and TM.6 / entry record.",
      "TM.30 address notification and rental or ownership evidence.",
      "Bank documents in the format and seasoning period required.",
      "Health insurance documents where the category requires them.",
      "Marriage or dependency documents for family-based categories.",
    ],
    pitfalls: [
      "Bank evidence that does not meet the required seasoning period.",
      "Missing TM.30 notification, which is checked before the extension.",
      "Filing too close to the permitted-stay expiry.",
      "Believing an extension can be guaranteed — the decision is the officer's.",
    ],
    remote: "File preparation and review are remote; the immigration appointment is attended in person.",
  },
  corporate: {
    label: "Corporate and company document service",
    definition:
      "Corporate document work covers company records, affidavits, DBD extracts, board resolutions and their certification for use abroad — often for opening accounts, tenders, distributorships or overseas subsidiaries.",
    chain: [
      "Confirm the receiving party's exact document list and how recent it must be.",
      "Obtain fresh DBD extracts and affidavits.",
      "Prepare resolutions or powers of attorney in the wording the counterparty accepts.",
      "Notarial certification of signatures and copies where required.",
      "MFA legalization and destination-embassy certification for cross-border use.",
    ],
    checklist: [
      "Company affidavit and DBD certificate (recent issue).",
      "Shareholder list and objectives extract.",
      "Passports or Thai IDs of authorised directors.",
      "Draft resolution or contract in final wording.",
      "The counterparty's written document requirements.",
    ],
    pitfalls: [
      "Extracts older than the counterparty's validity window.",
      "Signing before the certification appointment.",
      "Resolutions signed by a director without authority under the affidavit.",
      "Translations that omit the seal and stamp text on the Thai extract.",
    ],
    remote: "Everything except signature witnessing is handled remotely.",
  },
  immigration: {
    label: "Immigration document preparation and advisory",
    definition:
      "Immigration filings — extensions, re-entry permits, change of status, notifications — each have their own evidence list. We prepare and sequence the file; the Immigration Bureau decides.",
    chain: [
      "Confirm the filing type, office of jurisdiction and evidence list.",
      "Review current permitted stay and prior filings for consistency.",
      "Prepare and certify the supporting documents.",
      "Assemble in filing order with a checklist for the counter.",
      "Follow up and respond to any additional-evidence request.",
    ],
    checklist: [
      "Passport with all visa and stamp pages.",
      "TM.30 address notification and accommodation evidence.",
      "Employment, study or family documents relevant to the category.",
      "Photographs to the current specification.",
      "Power of attorney where an agent files on your behalf.",
    ],
    pitfalls: [
      "Filing at the wrong office of jurisdiction.",
      "Inconsistent addresses between TM.30, lease and application.",
      "Letting the permitted stay lapse while gathering evidence.",
      "Relying on outdated online checklists rather than the office's current one.",
    ],
    remote: "Preparation and advisory remote; counter appointments in person.",
  },
  estate: {
    label: "Estate, will and inheritance document service",
    definition:
      "Thai estate matters combine court and registry steps: a will (or intestate rules), an estate administrator appointment by the court, then transfer of each asset class at its own registry. Cross-border estates add translation and legalization.",
    chain: [
      "Map the estate: assets, asset locations, heirs and any existing will.",
      "Confirm whether a court-appointed administrator is required for each asset.",
      "Assemble civil-status and heirship evidence, with certified translations for foreign documents.",
      "File the administrator petition and attend the hearing through counsel.",
      "Transfer each asset at the relevant registry with the court order.",
    ],
    checklist: [
      "Death certificate and its certified translation where issued abroad.",
      "Will, if any, and details of where the original is held.",
      "Heirship evidence: house registration, birth and marriage certificates.",
      "Asset evidence: title deeds, bank statements, share registers.",
      "Passports or IDs of all heirs.",
    ],
    pitfalls: [
      "Attempting a bank or land transfer before the administrator order exists.",
      "Foreign documents without legalized certified Thai translation.",
      "Missing heirs, which can invalidate the distribution.",
      "Assuming a foreign will operates over Thai land without a Thai process.",
    ],
    remote:
      "Document assembly and drafting are remote; court hearings are attended by counsel, with your presence only where the court requires it.",
  },
  civilreg: {
    label: "Civil registration document service",
    definition:
      "Civil registration covers birth, marriage, divorce, death, name change and house-registration records held by district offices. Each correction or extract has its own evidence list, and cross-border use adds translation and legalization.",
    chain: [
      "Identify the record type and the district office holding it.",
      "Order a certified extract or file the correction petition.",
      "Assemble the supporting evidence the office lists.",
      "Certified translation for overseas use.",
      "MFA legalization and destination-embassy certification if required.",
    ],
    checklist: [
      "Thai ID and house registration, or passport for foreign nationals.",
      "The existing certificate or extract, if you hold one.",
      "Evidence supporting the correction (school, hospital or court documents).",
      "Power of attorney where we request on your behalf.",
    ],
    pitfalls: [
      "Requesting from an office that does not hold the original record.",
      "Corrections filed without documentary evidence of the correct data.",
      "Translating an extract that is later reissued, forcing a second round.",
      "Inconsistent transliteration across family members' documents.",
    ],
    remote: "Requests and follow-up handled for you; some corrections require the record holder in person.",
  },
  family: {
    label: "Family law document service",
    definition:
      "Family matters — divorce, child custody, legitimation, guardianship, prenuptial agreements — are decided either at the district office (by consent) or by the court. Choosing the correct route first avoids most delay.",
    chain: [
      "Assess whether the matter can be completed by consent at the district office or needs the court.",
      "Draft the agreement or petition in enforceable wording.",
      "Assemble civil-status evidence, with certified translations where foreign.",
      "Register at the district office or file with the court.",
      "Obtain certified copies, translation and legalization for use abroad.",
    ],
    checklist: [
      "Marriage certificate and, where relevant, prenuptial documents.",
      "Birth certificates of children.",
      "Thai IDs, house registration, passports of both parties.",
      "Evidence relevant to custody or support arrangements.",
      "Any foreign court order, with certified translation.",
    ],
    pitfalls: [
      "Consent divorce registered without a written agreement on assets and custody.",
      "Foreign orders used in Thailand without a recognition step.",
      "Wording that a district office or bank later refuses to act on.",
      "Assuming Thai and overseas records update automatically — they do not.",
    ],
    remote: "Drafting and evidence review remote; registration and hearings require attendance.",
  },
  accounting: {
    label: "Accounting, tax and audit document service",
    definition:
      "Accounting document work covers bookkeeping records, financial statements, audit files and tax filings, plus their certification and translation when a bank, tender board or overseas parent company asks for them.",
    chain: [
      "Confirm which statements and periods the requesting party needs.",
      "Reconcile the records and prepare the statement set.",
      "Auditor sign-off where a certified statement is required.",
      "Certified translation of statements and audit notes.",
      "Notarial certification and, for overseas use, MFA legalization.",
    ],
    checklist: [
      "Company affidavit and tax registration documents.",
      "Trial balance, ledgers and bank statements for the period.",
      "Prior-year filed statements and tax returns.",
      "The requesting party's format requirements.",
    ],
    pitfalls: [
      "Submitting management accounts where an audited set was required.",
      "Translated statements that lose the auditor's note wording.",
      "Certifying an interim version that is later restated.",
      "Missing the filing calendar while gathering documents.",
    ],
    remote: "Handled remotely end to end, with paper certified sets couriered.",
  },
  interpreter: {
    label: "Court and appointment interpreter service",
    definition:
      "Interpreting is spoken language support at an appointment — court hearing, land office, district office, notarial signing, bank or immigration counter. It is separate from certified translation of documents, and many appointments need both.",
    chain: [
      "Confirm the venue, language pair, date and expected duration.",
      "Brief the interpreter on the subject matter and terminology in advance.",
      "Provide the document set so terminology matches the written file.",
      "Attend the appointment with the interpreter.",
      "Follow-up notes and any written translation the venue asks for.",
    ],
    checklist: [
      "Venue, room and appointment time.",
      "Language pair and dialect where relevant.",
      "Documents to be discussed, in advance.",
      "Names and roles of attending parties for correct address forms.",
    ],
    pitfalls: [
      "Booking too late for a specialist language pair.",
      "No advance document set, so terminology is improvised at the counter.",
      "Assuming an interpreter can also certify the written translation on the spot.",
      "Under-estimating duration, which affects scheduling.",
    ],
    remote: "Remote interpreting is possible where the venue allows it; courts and registries usually require attendance.",
  },
  embassy: {
    label: "Embassy and consular document service",
    definition:
      "Embassy certification is the last link for many outbound files: after MFA legalization, the destination country's embassy in Thailand certifies the document. Each embassy sets its own booking, wording and translator requirements.",
    chain: [
      "Confirm the embassy's current requirements, including any approved translator list.",
      "Prepare the Thai original and certified translation accordingly.",
      "Complete MFA legalization first, where the embassy requires it.",
      "Book and submit at the embassy; collect the certified set.",
      "Deliver with a submission checklist for the receiving authority.",
    ],
    checklist: [
      "Thai original with signature and seal.",
      "Certified translation into the required language.",
      "Passport or ID of the document holder.",
      "Embassy forms and any authorisation letter.",
    ],
    pitfalls: [
      "Using a translator outside an embassy's approved list.",
      "Submitting to the embassy before MFA legalization.",
      "Missing embassy-specific form or photo requirements.",
      "Assuming all embassies follow the same sequence — they do not.",
    ],
    remote: "Preparation and submission handled for you where the embassy permits representation.",
  },
  ip: {
    label: "Trademark, copyright and IP document service",
    definition:
      "IP work in Thailand centres on trademark filing and maintenance at the Department of Intellectual Property, plus copyright recordal and assignment or licence documents — many of which need notarial certification and legalization when signed abroad.",
    chain: [
      "Clearance search and class selection before filing.",
      "Prepare the application, specimen and power of attorney.",
      "File and monitor examination; respond to office actions.",
      "Certify and legalize foreign-signed powers, assignments or licences.",
      "Record assignments, licences and renewals as they fall due.",
    ],
    checklist: [
      "Mark specimen in the exact form to be used.",
      "Applicant corporate documents or ID.",
      "Goods and services list for class selection.",
      "Signed power of attorney (notarised and legalized when signed abroad).",
      "Evidence of first use, where relied on.",
    ],
    pitfalls: [
      "Filing without a clearance search, then facing an opposition.",
      "Class lists that are too narrow to cover the real business.",
      "Foreign-signed powers without required certification.",
      "Missing renewal or response deadlines.",
    ],
    remote: "Filing and prosecution are handled remotely; only signature certification may need attendance.",
  },
  litigation: {
    label: "Litigation support and court document service",
    definition:
      "Litigation support covers pleadings, evidence sets, certified translations of foreign evidence, notarised affidavits and powers of attorney — prepared so the court accepts them without a formality objection.",
    chain: [
      "Case assessment: forum, limitation period and evidence gaps.",
      "Prepare pleadings and the evidence bundle in court format.",
      "Certified translation of every foreign-language exhibit.",
      "Notarial certification and legalization of documents signed abroad.",
      "Filing, hearing attendance by counsel and post-judgment steps.",
    ],
    checklist: [
      "Contracts, correspondence and the full document trail.",
      "Passports or corporate documents of the parties.",
      "Power of attorney for counsel (certified where signed abroad).",
      "Any foreign judgment or arbitral award, with certified translation.",
    ],
    pitfalls: [
      "Foreign exhibits filed without certified Thai translation.",
      "Powers of attorney signed abroad without notarisation and legalization.",
      "Losing a limitation period while collecting evidence.",
      "Expecting an outcome guarantee — no lawyer can give one.",
    ],
    remote: "Drafting and evidence work remote; hearings are attended by counsel.",
  },
  pdpa: {
    label: "PDPA and data-protection document service",
    definition:
      "Thailand's Personal Data Protection Act requires a documented basis for processing personal data: privacy notices, consent records, data-processing agreements, records of processing and breach procedures.",
    chain: [
      "Data mapping: what personal data you hold, why, and where it flows.",
      "Select and document the lawful basis for each processing purpose.",
      "Draft the privacy notice, consent forms and data-processing agreements.",
      "Set up the record of processing activities and breach procedure.",
      "Staff briefing and a review cycle as processing changes.",
    ],
    checklist: [
      "List of systems and vendors holding personal data.",
      "Existing privacy notice and consent wording.",
      "Vendor contracts covering data processing.",
      "Cross-border transfer details, if any.",
    ],
    pitfalls: [
      "Copying a foreign GDPR notice without mapping it to PDPA requirements.",
      "Consent wording bundled so it cannot be relied on.",
      "No record of processing, which is what a review asks for first.",
      "Vendor contracts with no data-processing terms.",
    ],
    remote: "Fully remote, with workshops by video call.",
  },
  "misc-official": {
    label: "Other official document services",
    definition:
      "This covers the official documents that do not fit a single ministry: licences, permits, certificates, records requests and their certification and translation for use in Thailand or abroad.",
    chain: [
      "Identify the issuing authority and its current form set.",
      "Assemble the evidence list and check identity documents.",
      "File and track the reference number.",
      "Certified translation where the document leaves Thailand.",
      "MFA legalization and embassy certification if required.",
    ],
    checklist: [
      "Passport or Thai ID of the applicant.",
      "Existing certificate, licence or reference number.",
      "Supporting evidence named on the authority's form.",
      "Power of attorney for filing on your behalf.",
    ],
    pitfalls: [
      "Filing at the wrong authority, which restarts the queue.",
      "Incomplete evidence lists.",
      "Translating before the Thai original is final.",
      "Overlooking validity windows on supporting documents.",
    ],
    remote: "Filing and follow-up handled for you where representation is permitted.",
  },
  council: {
    label: "Lawyers Council certification service",
    definition:
      "Certain certifications are performed by lawyers licensed by the Lawyers Council of Thailand — including notarial services attorney work and lawyer certification of documents and copies for use abroad.",
    chain: [
      "Confirm what the receiving authority wants certified and in what wording.",
      "Verify the signatory's identity and authority.",
      "Certification by a Lawyers Council-licensed attorney with register entry.",
      "MFA legalization and destination-embassy certification where required.",
      "Delivery of certified originals with a checklist.",
    ],
    checklist: [
      "Passport or Thai ID of each signatory.",
      "The unsigned document in final wording.",
      "Corporate authority documents for company signings.",
      "The receiving authority's written requirements, if held.",
    ],
    pitfalls: [
      "Documents signed before the certification appointment.",
      "Certifying a copy when an original was required.",
      "Name spelling inconsistent with the passport.",
      "Skipping legalization when the destination expects it.",
    ],
    remote: "Review and downstream steps remote; signature witnessing in person.",
  },
  "due-diligence": {
    label: "Due-diligence and verification service",
    definition:
      "Due diligence verifies what a counterparty asserts: company standing, directors and shareholders, land title, litigation exposure and document authenticity — using registry records rather than representations.",
    chain: [
      "Define scope: entity, assets and risk questions to answer.",
      "Pull registry records (DBD, land office, court indexes) as available.",
      "Verify document authenticity and signing authority.",
      "Certified translation of key findings and source documents.",
      "Written report with issues ranked and recommended next steps.",
    ],
    checklist: [
      "Counterparty name, registration number and address.",
      "Title deed or asset identifiers.",
      "Draft contract and any representations given.",
      "Existing documents supplied by the counterparty.",
    ],
    pitfalls: [
      "Relying on documents supplied by the counterparty without registry verification.",
      "Skipping the land-office check on encumbrances.",
      "Signing before the report is complete.",
      "Assuming a search result is permanent — records change.",
    ],
    remote: "Registry work and reporting are remote; site visits arranged on request.",
  },
  pension: {
    label: "Pension and overseas benefit document service",
    definition:
      "Overseas pension and benefit bodies periodically require proof of life, proof of address, marital-status confirmation or bank verification, often certified and legalized before they accept it.",
    chain: [
      "Confirm the exact form and certification level the pension body requires.",
      "Complete the form and gather identity and address evidence.",
      "Notarial or lawyer certification of signature and copies.",
      "MFA legalization and embassy certification where the body requires it.",
      "Submit or courier to the pension body and confirm receipt.",
    ],
    checklist: [
      "Passport and Thai residence or immigration documents.",
      "The pension body's form, unsigned.",
      "Proof of address in Thailand.",
      "Marriage or civil-status documents where the benefit depends on them.",
      "Bank details in the format requested.",
    ],
    pitfalls: [
      "Signing the life certificate before the certification appointment.",
      "Missing the annual deadline, which can suspend payments.",
      "Certification level below what the body requires.",
      "Address evidence that does not match the immigration record.",
    ],
    remote: "Everything except signature witnessing is handled for you.",
  },
  "work-permit": {
    label: "Work permit document service",
    definition:
      "A Thai work permit is issued by the Department of Employment on an employer-and-employee evidence set. We prepare and sequence the documents and advise on the visa steps around it; the decision remains the authority's.",
    chain: [
      "Confirm permit type, employer eligibility and the current document list.",
      "Prepare employer documents: affidavit, statements, employee ratio evidence.",
      "Prepare employee documents: degree, references, medical certificate, photos.",
      "Certified translation and legalization of foreign qualifications where required.",
      "File, follow up and align the permit with the visa or extension timeline.",
    ],
    checklist: [
      "Employer affidavit, DBD extract and VAT registration.",
      "Employee passport with the correct visa category.",
      "Degree certificate and transcripts, with certified translation.",
      "Medical certificate to the current specification.",
      "Employment contract and job description.",
    ],
    pitfalls: [
      "Entering on a tourist visa where the permit route needs a different category.",
      "Foreign degrees without certified translation and legalization.",
      "Employer ratio or capital requirements not met at filing.",
      "Permit and visa expiry dates falling out of alignment.",
    ],
    remote: "Preparation and filing support remote; medical and counter steps in person.",
  },
  expert: {
    label: "Specialist and complex-case document service",
    definition:
      "Some files do not fit a standard route: multi-country chains, unusual authority wording, refused submissions or documents needing several certifications in a specific order. These are handled as advisory-led cases.",
    chain: [
      "Case review: what the receiving authority actually requires, in writing.",
      "Design the certification chain and the order it must run in.",
      "Fix defects in existing documents before any further certification.",
      "Execute the chain: translation, notarial certification, MFA legalization, embassy.",
      "Deliver with a submission memo the receiving authority can follow.",
    ],
    checklist: [
      "Every document already produced, including rejected versions.",
      "The written rejection reason or requirement letter, if any.",
      "Passports or corporate documents of the parties.",
      "The destination country, authority and deadline.",
    ],
    pitfalls: [
      "Repeating a certification chain that was refused, without addressing the reason.",
      "Certifying in the wrong order, so the final version is uncovered.",
      "Acting on second-hand requirements instead of the authority's own wording.",
      "Leaving no time for a second round where the file is complex.",
    ],
    remote: "Assessment and chain execution remote; only witnessing steps require attendance.",
  },
};
